/* eslint-disable react/prop-types */
import * as React from "react";
import { useContext, useEffect, useState } from "react";

import EditIcon from "@mui/icons-material/Edit";
import InfoIcon from "@mui/icons-material/Info";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import SettingsBackupRestoreIcon from "@mui/icons-material/SettingsBackupRestore";
import {
  Alert,
  Box,
  Collapse,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import Paper from "@mui/material/Paper";
import { useParams } from "react-router-dom";

import {
  AdminRestriction,
  AdminRestrictionAllowList,
  AdminRestrictionFeature,
  AdminRestrictionQuota,
  AdminRestrictionTimeWindow,
} from "@safetyculture/s12-apis-connect-web/s12/restrictions/admin/v1/restrictions_pb";
import { RestrictionQuota } from "@safetyculture/s12-apis-connect-web/s12/restrictions/v1/common_pb";
import { ActionType } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/action_pb";

import {
  getOrgRestrictions as getOrgRestrictionsApi,
  getUserRestrictions as getUserRestrictionsApi,
} from "api/endpoints/restrictions";
import { callOptionsHeaders } from "api/helpers";
import {
  Copyable,
  InfoHint,
  cardTitleSx,
  valueMonoSx,
  valueSx,
} from "components/org-display/dashboard/cards/card-shell";
import {
  orgQuotaBuckets,
  quotaUsage,
} from "components/org-display/tabs/components/use-org-ai-credit";
import EditOrgRestriction from "components/shared/restrictions/edit-org-restriction";
import RemoveOrgRestrictionOverride from "components/shared/restrictions/remove-org-restriction-override";
import { NO_DISPLAY_DATA } from "constants/index";
import {
  RESTRICTION_DISPLAY_NAMES,
  RestrictionNameKey,
} from "constants/restriction-display-names";
import { RESTRICTION_TYPE_DESCRIPTIONS } from "constants/restriction-type-descriptions";
import {
  EDITABLE_ORG_RESTRICTION_NAMES,
  REMOVABLE_ORG_RESTRICTION_NAMES,
} from "constants/restrictions";
import { PermissionsContext } from "context/permissions-context";
import { SnackbarContext } from "context/snackbar-context";
import { toUUID } from "helpers/s12-helpers";
import { convertTimestampToDate, formatDateTimeDisplay } from "helpers/time";
import { UrlParamTypes } from "types/org-display";
import { UserUrlParamTypes } from "types/user-display";

const DEV_MOCK_RESTRICTIONS: Array<[string, AdminRestriction]> = [];

const HEADINGS: Array<{ title: string; key: string }> = [
  { title: "", key: "heading-expand" },
  { title: "Restriction", key: "heading-restriction" },
  { title: "Type", key: "heading-type" },
  { title: "Feature Flag", key: "heading-feature-flag" },
  { title: "Details", key: "heading-details" },
  { title: "Reset Date", key: "heading-reset-date" },
  { title: "Actions", key: "heading-actions" },
];

const displayQuotaResetDate = (quota: RestrictionQuota | undefined) => {
  if (!quota?.resetTime) return NO_DISPLAY_DATA;
  return <>{formatDateTimeDisplay(convertTimestampToDate(quota.resetTime))}</>;
};

const displayAllowedIds = (ids: string[]) =>
  ids.map((id) => (
    <Box key={id}>
      <span>{id}</span>
    </Box>
  ));

const ExpandedAllowList = ({
  restriction,
}: {
  restriction: AdminRestrictionAllowList;
}) => {
  if (!restriction.userRestriction) return null;
  const { userRestriction, defaultCapacity } = restriction;
  return (
    <Box sx={{ margin: 1 }}>
      <Typography variant="body1" gutterBottom>
        <b>Allow List</b>
      </Typography>
      <Table size="small">
        <TableBody>
          <TableRow>
            <TableCell>
              <b>Allowed IDs</b>
            </TableCell>
            <TableCell
              sx={{ fontFamily: "monospace", fontSize: "small" }}
              data-testid="allowed-ids"
            >
              {displayAllowedIds(userRestriction.allowedIds)}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              <b>Total List Capacity</b>
              <Tooltip title="This is the maximum capacity of the allow list for the user.">
                <IconButton size="small" sx={{ padding: "0px" }}>
                  <InfoIcon />
                </IconButton>
              </Tooltip>
            </TableCell>
            <TableCell data-testid="total-allowed-ids-capacity">
              <b>{userRestriction.maxAllowedIds}</b>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Capacity Used</TableCell>
            <TableCell data-testid="capacity-used">
              {userRestriction.allowedIds.length}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Capacity Remaining</TableCell>
            <TableCell data-testid="capacity-remaining">
              {defaultCapacity - userRestriction.allowedIds.length}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Box>
  );
};

const ExpandedQuotaMap = ({
  restriction,
}: {
  restriction: AdminRestriction;
}) => {
  const buckets = orgQuotaBuckets(restriction);
  if (buckets.length === 0) return null;
  return (
    <Box sx={{ margin: 1 }}>
      <Typography variant="body1" gutterBottom>
        <b>Buckets</b>
      </Typography>
      <Table size="small" aria-label="quota-map-buckets-table">
        <TableHead>
          <TableRow>
            <TableCell>
              <b>Bucket</b>
            </TableCell>
            <TableCell>
              <b>Details</b>
            </TableCell>
            <TableCell>
              <b>Reset Date</b>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {buckets.map(({ key, label, quota }) => (
            <TableRow key={key} data-testid={`quota-map-${key}`}>
              <TableCell>{label}</TableCell>
              <TableCell>
                {quotaUsage(quota)}/{quota.limit} {quota.unit} used ·{" "}
                {quota.remaining} remaining
              </TableCell>
              <TableCell>{displayQuotaResetDate(quota)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

const ExpandSummaryLink = ({
  onClick,
  testId,
  children,
}: {
  onClick: () => void;
  testId: string;
  children: React.ReactNode;
}) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    data-testid={testId}
    sx={{
      cursor: "pointer",
      color: "primary.main",
      textDecoration: "underline",
      border: "none",
      background: "none",
      padding: 0,
      font: "inherit",
      textAlign: "left",
    }}
  >
    {children}
  </Box>
);

const displayRestrictionSummary = (
  restriction: AdminRestriction,
  open: boolean,
  setOpen: React.Dispatch<React.SetStateAction<boolean>>,
) => {
  switch (restriction.restriction.case) {
    case "allowList": {
      const v = restriction.restriction.value as AdminRestrictionAllowList;
      return (
        <ExpandSummaryLink
          onClick={() => setOpen(!open)}
          testId="allow-list-summary-button"
        >
          {v.userRestriction?.allowedIds.length}/
          {v.userRestriction?.maxAllowedIds} allowed ids
        </ExpandSummaryLink>
      );
    }
    case "timeWindow": {
      const v = restriction.restriction.value as AdminRestrictionTimeWindow;
      const secs = v.userRestriction?.maxTimeWindow?.seconds;
      return <>{secs ? Number(secs) / (3600 * 24) : "--"}&nbsp;days</>;
    }
    case "feature": {
      const v = restriction.restriction.value as AdminRestrictionFeature;
      return (
        <>{v.userRestriction?.isRestricted ? "restricted" : "not restricted"}</>
      );
    }
    case "quota": {
      const v = restriction.restriction.value as AdminRestrictionQuota;
      const q = v?.userRestriction;
      return (
        <>
          {q?.remaining}/{q?.limit} {q?.unit} remaining
        </>
      );
    }
    case "quotaMap": {
      const buckets = orgQuotaBuckets(restriction);
      const n = buckets.length;
      return (
        <ExpandSummaryLink
          onClick={() => setOpen(!open)}
          testId="quota-map-summary-button"
        >
          {n} {n === 1 ? "bucket" : "buckets"}
        </ExpandSummaryLink>
      );
    }
  }
};

const displayResetDate = (restriction: AdminRestriction): React.ReactNode => {
  if (restriction.restriction.case === "quota") {
    const v = restriction.restriction.value as AdminRestrictionQuota;
    return displayQuotaResetDate(v?.userRestriction);
  }
  return NO_DISPLAY_DATA;
};

function Row({
  row,
  canEdit,
  onEdit,
  onRemoveOverride,
}: {
  row: [string, AdminRestriction];
  canEdit: boolean;
  onEdit: (name: string, restriction: AdminRestriction) => void;
  onRemoveOverride: (name: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [name, restriction] = row;
  const { copyToClipboard } = useContext(SnackbarContext);
  const isExpandable =
    restriction.restriction.case === "allowList" ||
    restriction.restriction.case === "quotaMap";
  const isEditableShape =
    restriction.restriction.case === "feature" ||
    restriction.restriction.case === "timeWindow" ||
    restriction.restriction.case === "quota";
  const showEdit =
    canEdit && isEditableShape && EDITABLE_ORG_RESTRICTION_NAMES.has(name);
  const showRemove =
    canEdit && isEditableShape && REMOVABLE_ORG_RESTRICTION_NAMES.has(name);

  const expandedContent = (() => {
    switch (restriction.restriction.case) {
      case "allowList":
        return (
          <ExpandedAllowList
            restriction={
              restriction.restriction.value as AdminRestrictionAllowList
            }
          />
        );
      case "quotaMap":
        return <ExpandedQuotaMap restriction={restriction} />;
      default:
        return null;
    }
  })();

  if (!restriction.restriction.case) return null;

  return (
    <>
      <TableRow
        sx={{
          height: "72px",
          ...(isExpandable && { "& > *": { borderBottom: "unset" } }),
        }}
      >
        <TableCell padding="checkbox">
          {isExpandable && (
            <IconButton
              aria-label="expand row"
              size="small"
              onClick={() => setOpen(!open)}
              data-testid="left-expand-button"
            >
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
        </TableCell>
        <TableCell component="th" scope="row">
          {(() => {
            const rawName = name.replace("RESTRICTION_NAME_", "");
            const displayName =
              RESTRICTION_DISPLAY_NAMES[name as RestrictionNameKey];
            const rawNameCopyable = (
              <Copyable
                value={rawName}
                label="restriction key"
                onCopy={copyToClipboard}
              />
            );
            if (!displayName) {
              return <Box sx={valueMonoSx}>{rawNameCopyable}</Box>;
            }
            return (
              <Box>
                <Typography sx={valueSx}>{displayName}</Typography>
                <Box sx={{ ...valueMonoSx, color: "text.secondary" }}>
                  {rawNameCopyable}
                </Box>
              </Box>
            );
          })()}
        </TableCell>
        <TableCell>
          <Box sx={{ ...valueSx, display: "flex", alignItems: "center" }}>
            {restriction.restriction.case}
            <InfoHint
              title={
                RESTRICTION_TYPE_DESCRIPTIONS[restriction.restriction.case]
              }
            />
          </Box>
        </TableCell>
        <TableCell>
          <Box sx={valueSx}>{restriction.featureFlagName}</Box>
        </TableCell>
        <TableCell>
          <Box sx={valueSx}>
            {displayRestrictionSummary(restriction, open, setOpen)}
          </Box>
        </TableCell>
        <TableCell>
          <Box sx={valueSx}>{displayResetDate(restriction)}</Box>
        </TableCell>
        <TableCell>
          {showEdit && (
            <IconButton
              aria-label="edit restriction"
              size="small"
              onClick={() => onEdit(name, restriction)}
              data-testid={`edit-restriction-${name}`}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          )}
          {showRemove && (
            <Tooltip title="Remove the per-org override and fall back to the plan default">
              <IconButton
                aria-label="remove restriction override"
                size="small"
                onClick={() => onRemoveOverride(name)}
                data-testid={`remove-override-${name}`}
              >
                <SettingsBackupRestoreIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </TableCell>
      </TableRow>
      {isExpandable && (
        <TableRow>
          <TableCell
            style={{ paddingBottom: 0, paddingTop: 0 }}
            colSpan={HEADINGS.length}
          >
            <Collapse in={open} timeout="auto" unmountOnExit>
              {expandedContent}
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

type RestrictionsTabParams = {
  isUser: boolean;
};

const RestrictionsTab = ({ isUser }: RestrictionsTabParams) => {
  const { regionCode: userRegionCode, userId } = useParams<UserUrlParamTypes>();
  const { regionCode: orgRegionCode, roleId } = useParams<UrlParamTypes>();
  const [isLoading, setIsLoading] = useState(false);
  const [restrictions, setRestrictions] = useState<
    Array<[string, AdminRestriction]>
  >([]);
  const [editingRestriction, setEditingRestriction] = useState<
    [string, AdminRestriction] | null
  >(null);
  const [removingOverrideName, setRemovingOverrideName] = useState<
    string | null
  >(null);

  const { showError } = useContext(SnackbarContext);
  const { hasPermissions, permissions } = useContext(PermissionsContext);

  // UpdateOrgRestriction is org-only (see FG-7722) — there is no per-user
  // equivalent, so editing is never offered on the user-scoped tab.
  const canUpdateOrgRestriction =
    !isUser &&
    !permissions.isPermissionsLoading &&
    hasPermissions([ActionType.UPDATE_ORG_RESTRICTION]);

  const getUserRestrictions = () => {
    setIsLoading(true);
    getUserRestrictionsApi(
      {
        userId: toUUID(userId),
        restrictionNames: [],
      },
      callOptionsHeaders(userRegionCode, userId),
    )
      .then((restrictionsResponse) => {
        const restrictions = restrictionsResponse.restrictions;
        const sortedRestrictions = Object.entries(restrictions).sort(
          ([keyA], [keyB]) => keyA.localeCompare(keyB),
        );
        const validRestrictions = sortedRestrictions.filter(
          (r) => r[0] && r[1]?.restriction.case !== undefined,
        );
        setRestrictions([...validRestrictions, ...DEV_MOCK_RESTRICTIONS]);
      })
      .catch((err) => {
        showError(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const getOrgRestrictions = () => {
    setIsLoading(true);
    getOrgRestrictionsApi(
      {
        organizationId: toUUID(roleId),
        restrictionNames: [],
      },
      callOptionsHeaders(orgRegionCode, roleId),
    )
      .then((restrictionsResponse) => {
        const restrictions = restrictionsResponse.restrictions;
        const sortedRestrictions = Object.entries(restrictions).sort(
          ([keyA], [keyB]) => keyA.localeCompare(keyB),
        );
        const validRestrictions = sortedRestrictions.filter(
          (r) => r[0] && r[1]?.restriction.case !== undefined,
        );
        setRestrictions([...validRestrictions, ...DEV_MOCK_RESTRICTIONS]);
      })
      .catch((err) => {
        showError(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (isUser) getUserRestrictions();
    else getOrgRestrictions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {!isLoading && restrictions.length === 0 ? (
        <Alert
          severity="info"
          variant="standard"
          style={{ margin: "2px" }}
          data-testid="no-restriction-alert"
        >
          User has no applicable restrictions.
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table aria-label="restrictions-table">
            <TableHead>
              <TableRow sx={{ bgcolor: "action.hover" }}>
                {HEADINGS.map(({ title, key }) => (
                  <TableCell
                    key={key}
                    padding={key === "heading-expand" ? "checkbox" : "normal"}
                  >
                    <Typography component="span" sx={cardTitleSx}>
                      {title}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={HEADINGS.length}>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              ) : (
                restrictions.map((restriction) => (
                  <Row
                    key={restriction[0]}
                    row={restriction}
                    canEdit={canUpdateOrgRestriction}
                    onEdit={(name, restriction) =>
                      setEditingRestriction([name, restriction])
                    }
                    onRemoveOverride={(name) => setRemovingOverrideName(name)}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {editingRestriction && (
        <EditOrgRestriction
          open
          organizationId={toUUID(roleId)}
          regionCode={orgRegionCode}
          restrictionName={editingRestriction[0]}
          restriction={editingRestriction[1]}
          onClose={() => setEditingRestriction(null)}
          onSuccess={() => {
            setEditingRestriction(null);
            getOrgRestrictions();
          }}
        />
      )}
      {removingOverrideName && (
        <RemoveOrgRestrictionOverride
          open
          organizationId={toUUID(roleId)}
          regionCode={orgRegionCode}
          restrictionName={removingOverrideName}
          onClose={() => setRemovingOverrideName(null)}
          onSuccess={() => {
            setRemovingOverrideName(null);
            getOrgRestrictions();
          }}
        />
      )}
    </>
  );
};

export default RestrictionsTab;
